/* ==========================================================================
   vd-charts.js — VitaDrip interactive data-viz + interaction engine
   Vanilla JS, no dependencies. SVG-based, progressively enhanced, idempotent.
   Hooks (all data-attribute driven):
     [data-vd-gauge]      ring/donut gauge  (data-vd-value, data-vd-max, data-vd-suffix)
     [data-vd-bars]       animated bar set  (children [data-vd-bar] with data-vd-value)
     [data-vd-chromo]     chromatogram line draw-in
     [data-vd-tabs]       tab switcher      (buttons [data-vd-tab=id], panels [data-vd-panel=id])
     [data-vd-batch-lookup] front-end batch verifier
   Animations run on scroll-in via IntersectionObserver and respect reduced-motion.
   ========================================================================== */
(function () {
  'use strict';
  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function once(el){ if(!el||el.dataset.vdcInit==='1') return false; el.dataset.vdcInit='1'; return true; }
  function onView(el, cb){
    if (REDUCE || !('IntersectionObserver' in window)) { cb(); return; }
    var io = new IntersectionObserver(function(es){
      es.forEach(function(e){ if(e.isIntersecting){ cb(); io.unobserve(e.target); } });
    }, { threshold: 0.3 });
    io.observe(el);
  }
  function ease(t){ return 1 - Math.pow(1 - t, 3); }
  function animate(dur, step){
    if (REDUCE){ step(1); return; }
    var start = null;
    function frame(ts){
      if(start===null) start=ts;
      var p = Math.min((ts-start)/dur, 1);
      step(ease(p));
      if(p<1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  /* ---- Ring / donut gauge ----------------------------------------------- */
  function initGauges(root){
    (root||document).querySelectorAll('[data-vd-gauge]').forEach(function(g){
      if(!once(g)) return;
      var val = parseFloat(g.getAttribute('data-vd-value'))||0;
      var max = parseFloat(g.getAttribute('data-vd-max'))||100;
      var suffix = g.getAttribute('data-vd-suffix')||'';
      var dec = (g.getAttribute('data-vd-decimals')!=null)? parseInt(g.getAttribute('data-vd-decimals'),10) : ((val%1)?1:0);
      var arc = g.querySelector('[data-vd-gauge-arc]');
      var num = g.querySelector('[data-vd-gauge-num]');
      var pct = Math.max(0, Math.min(val/max, 1));
      var len = arc ? arc.getTotalLength() : 0;
      if(arc){ arc.style.strokeDasharray = len; arc.style.strokeDashoffset = len; }
      onView(g, function(){
        animate(1100, function(p){
          if(arc) arc.style.strokeDashoffset = len*(1 - pct*p);
          if(num) num.textContent = (val*p).toFixed(dec) + suffix;
        });
      });
    });
  }

  /* ---- Animated bars ----------------------------------------------------- */
  function initBars(root){
    (root||document).querySelectorAll('[data-vd-bars]').forEach(function(wrap){
      if(!once(wrap)) return;
      var bars = wrap.querySelectorAll('[data-vd-bar]');
      onView(wrap, function(){
        bars.forEach(function(bar, i){
          var v = Math.max(0, Math.min(parseFloat(bar.getAttribute('data-vd-value'))||0, 100));
          var fill = bar.querySelector('[data-vd-bar-fill]');
          var num = bar.querySelector('[data-vd-bar-num]');
          setTimeout(function(){
            if(fill) fill.style.height = v + '%';
            if(num) animate(900, function(p){ num.textContent = Math.round(v*p) + (bar.getAttribute('data-vd-suffix')||''); });
          }, REDUCE?0:i*90);
        });
      });
    });
  }

  /* ---- Chromatogram line draw-in ---------------------------------------- */
  function initChromo(root){
    (root||document).querySelectorAll('[data-vd-chromo]').forEach(function(svg){
      if(!once(svg)) return;
      var path = svg.querySelector('[data-vd-chromo-line]');
      if(!path) return;
      var len = path.getTotalLength();
      path.style.strokeDasharray = len; path.style.strokeDashoffset = len;
      var fill = svg.querySelector('[data-vd-chromo-fill]');
      if(fill){ fill.style.opacity = 0; }
      onView(svg, function(){
        animate(1600, function(p){
          path.style.strokeDashoffset = len*(1-p);
          if(fill) fill.style.opacity = p*0.9;
        });
      });
    });
  }

  /* ---- Tabs -------------------------------------------------------------- */
  function initTabs(root){
    (root||document).querySelectorAll('[data-vd-tabs]').forEach(function(t){
      if(!once(t)) return;
      var tabs = Array.prototype.slice.call(t.querySelectorAll('[data-vd-tab]'));
      var panels = Array.prototype.slice.call(t.querySelectorAll('[data-vd-panel]'));
      function select(id){
        tabs.forEach(function(b){ b.setAttribute('aria-selected', b.getAttribute('data-vd-tab')===id ? 'true':'false'); });
        panels.forEach(function(p){ p.hidden = p.getAttribute('data-vd-panel')!==id; });
        // re-trigger any charts inside the now-visible panel
        var active = panels.filter(function(p){ return !p.hidden; })[0];
        if(active){ boot(active); }
      }
      tabs.forEach(function(b){
        b.addEventListener('click', function(){ select(b.getAttribute('data-vd-tab')); });
        b.addEventListener('keydown', function(e){
          var i = tabs.indexOf(b);
          if(e.key==='ArrowRight'||e.key==='ArrowDown'){ e.preventDefault(); tabs[(i+1)%tabs.length].focus(); tabs[(i+1)%tabs.length].click(); }
          if(e.key==='ArrowLeft'||e.key==='ArrowUp'){ e.preventDefault(); tabs[(i-1+tabs.length)%tabs.length].focus(); tabs[(i-1+tabs.length)%tabs.length].click(); }
        });
      });
      var initial = tabs.filter(function(b){ return b.getAttribute('aria-selected')==='true'; })[0] || tabs[0];
      if(initial) select(initial.getAttribute('data-vd-tab'));
    });
  }

  /* ---- Front-end batch lookup ------------------------------------------- */
  function initBatchLookup(root){
    (root||document).querySelectorAll('[data-vd-batch-lookup]').forEach(function(scope){
      if(!once(scope)) return;
      var form = scope.querySelector('form') || scope;
      var input = scope.querySelector('[data-vd-batch-input]');
      var records = Array.prototype.slice.call(scope.querySelectorAll('[data-vd-batch-record]'));
      var empty = scope.querySelector('[data-vd-batch-empty]');
      var prompt = scope.querySelector('[data-vd-batch-prompt]');
      function run(e){
        if(e) e.preventDefault();
        var q = (input && input.value || '').trim().toLowerCase();
        var found = false;
        records.forEach(function(r){
          var id = (r.getAttribute('data-vd-batch-id')||'').toLowerCase();
          var show = q && (id===q || (q.length>=3 && id.indexOf(q)!==-1));
          r.hidden = !show; if(show) found = true;
        });
        if(prompt) prompt.hidden = !!q;
        if(empty) empty.hidden = !(q && !found);
      }
      if(form) form.addEventListener('submit', run);
      if(input) input.addEventListener('input', function(){ if(!input.value.trim()){ run(); } });
    });
  }

  function boot(root){
    initGauges(root); initBars(root); initChromo(root); initTabs(root); initBatchLookup(root);
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded', function(){ boot(document); });
  else boot(document);
  document.addEventListener('shopify:section:load', function(e){ boot(e.target); });
  window.VDCharts = { boot: boot };
})();
